import { type Editor, Extension } from "@tiptap/core"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

/**
 * Definition line patterns where formatting should only be allowed AFTER the colon.
 * These are lines where the part before the colon is a label/identifier (not user-facing).
 */
const DEFINITION_LINE_PATTERNS = [
  /^Q\s+[^:]+:/i,           // Q label: text
  /^QUESTION\s+[^:]+:/i,    // QUESTION label: text
  /^TEXT\s+[^:]+:/i,        // TEXT label: text
  /^NOTE\s+[^:]+:/i,        // NOTE label: text
]

/**
 * Lines where NO formatting should be allowed (entire line is a label/keyword).
 */
const NO_FORMAT_LINE_PATTERNS = [
  /^LIST\s+\S+$/i,          // LIST name (no colon, entire line is label)
  /^SECTION\s*$/i,          // SECTION keyword
  /^EXCEPTIONS\s*$/i,       // EXCEPTIONS keyword
  /^#/,                     // Directive lines (#DEFINE, #LANGUAGE, etc.)
  /^SELECT\s+(ONE|MULTIPLE|RANGE)\s+FROM/i,  // SELECT keywords
  /^FILL\s*IN/i,            // FILL IN keywords
  /^PER\s+/i,               // PER keyword with list reference
]

/**
 * Check if the current cursor position allows bold/italic formatting.
 */
const canApplyFormattingAtPosition = (editor: Editor): boolean => {
  if (!editor) return false

  const { from, to } = editor.state.selection
  const doc = editor.state.doc

  let canFormat = true

  doc.nodesBetween(from, to, (node: ProseMirrorNode, pos: number) => {
    if (node.type.name === "paragraph") {
      const lineText = node.textContent
      const lineStart = pos + 1

      // Check if this is a no-format line
      for (const pattern of NO_FORMAT_LINE_PATTERNS) {
        if (pattern.test(lineText.trim())) {
          canFormat = false
          return false
        }
      }

      // Check if this is a definition line (has label before colon)
      for (const pattern of DEFINITION_LINE_PATTERNS) {
        if (pattern.test(lineText.trim())) {
          const colonIndex = lineText.indexOf(":")
          if (colonIndex !== -1) {
            const selectionStartInLine = from - lineStart
            const selectionEndInLine = to - lineStart

            if (selectionStartInLine <= colonIndex || selectionEndInLine <= colonIndex) {
              canFormat = false
              return false
            }
          }
          break
        }
      }
    }
  })

  return canFormat
}

/**
 * Extension that restricts bold/italic formatting to only user-facing text areas.
 * Blocks Ctrl+B and Ctrl+I keyboard shortcuts when cursor is in labels or keywords.
 */
const FormattingRestrictionExtension = Extension.create({
  name: "formattingRestriction",

  // Higher priority to intercept before Bold/Italic extensions
  priority: 1000,

  addKeyboardShortcuts() {
    return {
      "Mod-b": ({ editor }) => {
        if (!canApplyFormattingAtPosition(editor)) {
          return true // Block the shortcut
        }
        return false // Let Bold extension handle it
      },
      "Mod-B": ({ editor }) => {
        if (!canApplyFormattingAtPosition(editor)) {
          return true // Block the shortcut
        }
        return false // Let Bold extension handle it
      },
      "Mod-i": ({ editor }) => {
        if (!canApplyFormattingAtPosition(editor)) {
          return true // Block the shortcut
        }
        return false // Let Italic extension handle it
      },
      "Mod-I": ({ editor }) => {
        if (!canApplyFormattingAtPosition(editor)) {
          return true // Block the shortcut
        }
        return false // Let Italic extension handle it
      },
    }
  },
})

export default FormattingRestrictionExtension
